
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function SupportPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const supportCategories = [
    { id: 'all', name: 'All Topics', count: 24 },
    { id: 'getting-started', name: 'Getting Started', count: 8 },
    { id: 'ai-tools', name: 'AI Tools', count: 6 },
    { id: 'billing', name: 'Billing & Plans', count: 4 },
    { id: 'technical', name: 'Technical Issues', count: 6 }
  ];

  const faqs = [
    {
      id: 1,
      question: 'How do I get started with AI Consulting Authority?',
      answer: 'Simply sign up for a free trial, choose your plan, and our onboarding team will guide you through the setup process. You can start generating leads within 24 hours.',
      category: 'getting-started',
      popular: true
    },
    {
      id: 2,
      question: 'What AI tools are included in my subscription?',
      answer: 'All plans include lead generation AI, listing automation, market analytics, client management CRM, AI chatbots, and social media automation. Premium plans include AI staging and clone services.',
      category: 'ai-tools',
      popular: true
    },
    {
      id: 3,
      question: 'How accurate is the AI lead scoring?',
      answer: 'Our AI lead scoring achieves 94% accuracy by analyzing over 200 data points including browsing behavior, demographic data, and market indicators.',
      category: 'ai-tools',
      popular: true
    },
    {
      id: 4,
      question: 'Can I integrate with my existing CRM?',
      answer: 'Yes! We integrate with 50+ popular CRMs including Salesforce, HubSpot, Chime, Follow Up Boss, and more. Our team provides free integration setup.',
      category: 'technical',
      popular: false
    },
    {
      id: 5,
      question: 'What happens if I exceed my lead limit?',
      answer: 'You\'ll receive notifications at 80% and 95% usage. You can upgrade your plan or purchase additional leads. We never stop your service without notice.',
      category: 'billing',
      popular: false
    },
    {
      id: 6,
      question: 'How does AI property staging work?',
      answer: 'Upload property photos and our AI automatically adds furniture, decor, and lighting. Choose from 15+ design styles. Results are ready in under 10 minutes.',
      category: 'ai-tools',
      popular: true
    },
    {
      id: 7,
      question: 'Is there a setup fee?',
      answer: 'No setup fees on any plan. We provide free onboarding, training, and integration support to get you started quickly.',
      category: 'billing',
      popular: false
    },
    {
      id: 8,
      question: 'How do I cancel my subscription?',
      answer: 'You can cancel anytime from your dashboard settings. No cancellation fees. Your access continues until the end of your billing period.',
      category: 'billing',
      popular: false
    }
  ];

  const supportChannels = [
    {
      icon: 'ri-message-3-line',
      title: 'Live Chat',
      description: 'Get instant help from our support team',
      availability: 'Available 24/7',
      action: 'Start Chat'
    },
    {
      icon: 'ri-phone-line',
      title: 'Phone Support',
      description: 'Speak directly with our experts',
      availability: 'Mon-Fri 8AM-8PM EST',
      action: 'Call Now'
    },
    {
      icon: 'ri-mail-line',
      title: 'Email Support',
      description: 'Send us your questions and feedback',
      availability: 'Response within 2 hours',
      action: 'Send Email'
    },
    {
      icon: 'ri-video-line',
      title: 'Screen Share',
      description: 'Get personalized assistance',
      availability: 'By appointment',
      action: 'Schedule'
    }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            How can we help you?
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-blue-100 max-w-3xl mx-auto">
            Get the support you need to maximize your real estate AI tools
          </p>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto relative">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 transform -y-1/2 text-gray-400 text-xl"></i>
              <input
                type="text"
                placeholder="Search for answers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full py-4 pl-12 pr-4 rounded-lg text-gray-900 text-lg border-0 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Support Channels */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Contact Support</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {supportChannels.map((channel, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center border border-gray-100 hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${channel.icon} text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-semibold mb-2">{channel.title}</h3>
                <p className="text-gray-600 mb-3">{channel.description}</p>
                <p className="text-sm text-blue-600 mb-4">{channel.availability}</p>
                <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap">
                  {channel.action}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {supportCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-2 rounded-full transition-colors whitespace-nowrap ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-blue-50'
                }`}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>

          {/* FAQ List */}
          <div className="max-w-4xl mx-auto">
            {filteredFaqs.map((faq) => (
              <div key={faq.id} className="bg-white rounded-lg shadow-md mb-4 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex-1">
                      {faq.popular && (
                        <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mr-2">
                          Popular
                        </span>
                      )}
                      {faq.question}
                    </h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>

          {filteredFaqs.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-search-line text-4xl text-gray-400 mb-4"></i>
              <p className="text-xl text-gray-600 mb-2">No results found</p>
              <p className="text-gray-500">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
      </section>

      {/* Status Page */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">System Status</h2>
            
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-xl font-semibold">All Systems Operational</span>
                </div>
                <span className="text-gray-500">Last updated: 2 minutes ago</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { name: 'AI Lead Generation', status: 'operational', uptime: '99.9%' },
                  { name: 'Listing Automation', status: 'operational', uptime: '99.8%' },
                  { name: 'Market Analytics', status: 'operational', uptime: '99.9%' },
                  { name: 'AI Chatbots', status: 'operational', uptime: '99.7%' },
                  { name: 'Client Management', status: 'operational', uptime: '99.9%' },
                  { name: 'AI Staging Service', status: 'operational', uptime: '99.6%' }
                ].map((service, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                      <span className="font-medium">{service.name}</span>
                    </div>
                    <span className="text-sm text-gray-600">{service.uptime}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-blue-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Still need help?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Our expert support team is ready to assist you with any questions or technical issues
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors whitespace-nowrap">
              Schedule a Call
            </button>
            <button className="bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap">
              Send us a Message
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
